import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatMoney, formatQty } from '@shop/shared'
import { ApiError } from '@shop/api-client'
import { Banner, Empty, ErrorBanner, Spinner } from '@shop/ui'
import { ScanInput, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { PosHit, SaleDetail, SaleLineRequest } from '../pos/contracts'

/**
 * شاشةُ البيع — <b>بحثٌ فوق، وسلّةٌ تحت، ومجموعٌ يُقرأ من بعيد</b>.
 *
 * ⚠ <b>ولا مجموعَ يُحسب هنا.</b> السلّةُ تُرسل كاملةً إلى
 *   <c>PUT /sales/{id}/lines</c> بعد كلّ تغيير، وتُرسم من الجواب. والخادمُ
 *   يحسب الخصمَ والضريبةَ والمجموعَ بالتقريب نفسِه الذي يُحفظ به — وحسابُ
 *   نسخةٍ في المتصفّح يُنتج فرقَ فلسٍ يراه الزبونُ على الشاشة ولا يراه على
 *   الفاتورة.
 *
 * <b>وثمنُه رحلةٌ لكلّ تعديل</b> — وهو مقبولٌ على شبكةٍ محلّيّة، ومدفوعٌ عمدًا
 * مقابل أن يكون ما على الشاشة <b>هو</b> ما في القاعدة.
 */
export default function Sell({
  branchId,
  warehouseId,
  canOverridePrice,
  canDiscount,
  onPay,
  sale,
  onSaleChanged,
}: {
  branchId: string
  warehouseId: string
  canOverridePrice: boolean
  canDiscount: boolean
  onPay: () => void
  sale: SaleDetail | null
  onSaleChanged: (sale: SaleDetail | null) => void
}) {
  const [term, setTerm] = useState('')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [focusKey, setFocusKey] = useState(0)

  /** المُظلَّلُ في نتائج البحث — يتحرّك بالأسهم ويُضاف بـ<kbd>Enter</kbd>. */
  const [cursor, setCursor] = useState(0)

  /** المُظلَّلُ في السلّة — يتحرّك بالأسهم ويُحذف بـ<kbd>Delete</kbd>. */
  const [pick, setPick] = useState(0)

  /** السطورُ كما نبنيها — والجوابُ هو ما يُعرض. */
  const draft = useRef<SaleLineRequest[]>([])

  useEffect(() => {
    // والسلّةُ المحلّيّةُ تتبع الجوابَ دائمًا: إن أُلغيت أو أُتمّت تفرغ.
    if (!sale || sale.header.status !== 'DRAFT') {
      draft.current = []
    }
  }, [sale])

  // ── البحث ──────────────────────────────────────────────────────────────

  // ⚠ **ومهلةٌ قصيرةٌ قبل السؤال**: ماسحُ الباركود يكتب الرقمَ حرفًا حرفًا
  //   بسرعة، فبلا مهلةٍ يُطلق ثلاثةَ عشرَ نداءً لمسحةٍ واحدة.
  useEffect(() => {
    const timer = setTimeout(() => setTyped(term.trim()), 180)

    return () => clearTimeout(timer)
  }, [term])

  const search = useQuery({
    queryKey: ['lookup', branchId, warehouseId, typed],
    queryFn: () => pos.lookup(branchId, warehouseId, typed),
    enabled: typed.length >= 2,
  })

  // ── السلّة ─────────────────────────────────────────────────────────────

  const push = async (lines: SaleLineRequest[]) => {
    setBusy(true)
    setError(null)

    try {
      const current = sale?.header.status === 'DRAFT'
        ? sale
        : await pos.createSale({ branchId, warehouseId })

      onSaleChanged(await pos.setLines(current.header.id, lines))
      draft.current = lines
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
      setFocusKey((key) => key + 1)
    }
  }

  /**
   * المسحُ الفوريّ — <b>ولا ينتظر البحثَ أصلًا</b>.
   *
   * ⚠ <b>وهذا كان عطبَ الماسح:</b> الماسحُ يكتب الرمزَ ثمّ <kbd>Enter</kbd> في
   *   اللحظة نفسِها، والبحثُ ينتظر ١٨٠ مللي ثانيةً بعد آخر حرف — فحين تصل
   *   <kbd>Enter</kbd> تكون النتائجُ فارغةً بعد، <b>فلا يحدث شيء</b>. فيقف
   *   الكاشيرُ ينتظر القائمةَ ثمّ يضغط بالفأرة. والصنفُ الآن ينزل مباشرةً.
   *
   * وما لا يُطابَق رمزًا يعود إلى البحث بالاسم بلا رسالةِ خطأ — فمن كتب اسمًا
   * وضغط <kbd>Enter</kbd> لم يُخطئ.
   */
  const scan = async (code: string) => {
    setBusy(true)
    setError(null)

    try {
      const next = await pos.scan({
        branchId,
        warehouseId,
        code,
        saleId: sale?.header.status === 'DRAFT' ? sale.header.id : undefined,
      })

      onSaleChanged(next)
      draft.current = next.lines.map((l) => ({ variantId: l.variantId, qty: l.qty }))
      setTerm('')
      setTyped('')
    } catch (failure) {
      // ورمزٌ لا يُطابق صنفًا ليس خطأً: يُترك النصُّ ليُكمل البحثُ بالاسم.
      if (!(failure instanceof ApiError && failure.status === 404)) {
        setError(failure)
      }
    } finally {
      setBusy(false)
      setFocusKey((key) => key + 1)
    }
  }

  const add = (hit: PosHit) => {
    const lines = [...draft.current]
    const existing = lines.findIndex((line) => line.variantId === hit.variantId)

    // والمسحُ الثاني للصنف نفسِه يزيد الكميّة — لا يُنشئ سطرًا ثانيًا.
    if (existing >= 0) {
      lines[existing] = { ...lines[existing], qty: lines[existing].qty + 1 }
    } else {
      lines.push({ variantId: hit.variantId, qty: 1 })
    }

    setTerm('')
    setTyped('')
    void push(lines)
  }

  const setQty = (variantId: string, qty: number) => {
    const lines = qty <= 0
      ? draft.current.filter((line) => line.variantId !== variantId)
      : draft.current.map((line) => (line.variantId === variantId ? { ...line, qty } : line))

    void push(lines)
  }

  const applyOverride = (variantId: string, patch: Partial<SaleLineRequest>) => {
    void push(draft.current.map((line) => (line.variantId === variantId ? { ...line, ...patch } : line)))
  }

  // والنتائجُ تُخفى بالاشتقاق: نصٌّ أقصرُ من حرفين لا يعرض شيئًا، وما يُكتب
  // الآن يُخفي نتيجةَ ما كان قبله حتى تصل الجديدة.
  const shown: PosHit[] = term.trim().length < 2 || term.trim() !== typed ? [] : (search.data ?? [])

  const lines = sale?.header.status === 'DRAFT' ? sale.lines : []
  const total = sale?.header.status === 'DRAFT' ? sale.header.total : 0

  // ⚠ **والمؤشّرُ يُقصَّ على الطول دائمًا** — قائمةٌ قصُرت بعد حذفٍ تترك
  //   مؤشّرًا خارجها، فيصير <kbd>Delete</kbd> بلا أثرٍ ويبدو أنّه عطبان.
  const at = shown.length === 0 ? 0 : Math.min(cursor, shown.length - 1)
  const picked = lines.length === 0 ? -1 : Math.min(pick, lines.length - 1)

  /**
   * <b>لوحةُ المفاتيح أوّلًا</b> — والفأرةُ للاستثناء.
   *
   * ⚠ <b>والأسهمُ تتصرّف بحسب ما على الشاشة</b>: إن كانت نتائجُ بحثٍ ظاهرةً
   *   تنقّلت فيها، وإلّا تنقّلت في السلّة. ومفتاحان لمعنيين يربكان أكثرَ ممّا
   *   يُسرّعان.
   *
   * ⚠ <b>و<kbd>Enter</kbd> يمسح أوّلًا ويختار ثانيًا</b>: الماسحُ هو الأكثرُ
   *   استعمالًا، فلا يُؤخَّر خلف قائمةٍ قد تكون ظاهرة.
   */
  const onEnter = () => {
    const text = term.trim()

    if (text.length === 0) {
      // حقلٌ فارغٌ و<kbd>Enter</kbd> ⇒ ادفع. خطوةٌ أقلُّ لمن أنهى السلّة.
      if (lines.length > 0 && !busy) {
        onPay()
      }

      return
    }

    if (shown.length > 0) {
      add(shown[at])
      return
    }

    void scan(text)
  }

  const moveCursor = (step: 1 | -1) => {
    if (shown.length > 0) {
      setCursor((c) => {
        const next = Math.min(c, shown.length - 1) + step
        return next < 0 ? shown.length - 1 : next >= shown.length ? 0 : next
      })

      return
    }

    if (lines.length > 0) {
      setPick((c) => {
        const next = Math.min(c, lines.length - 1) + step
        return next < 0 ? lines.length - 1 : next >= lines.length ? 0 : next
      })
    }
  }

  const removePicked = () => {
    if (term.trim().length > 0 || picked < 0 || busy) {
      return
    }

    setQty(lines[picked].variantId, 0)
  }

  /**
   * مفاتيحُ الشاشة — <b>على النافذة لا على الحقل</b>.
   *
   * ⚠ و<kbd>F10</kbd> يجب أن يعمل أينما كان التركيز: كاشيرٌ لمس سطرًا بالإصبع
   *   ثمّ ضغط <kbd>F10</kbd> لا يفهم لماذا لم يحدث شيء.
   *
   * ⚠ <b>و<kbd>Delete</kbd> لا يُخطَف من حقل الكتابة</b>: من يكتب اسمًا
   *   ويمسح حرفًا لا يقصد حذفَ سطرٍ من الفاتورة. فالشرطُ أن يكون الحقلُ فارغًا.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'F10') {
        event.preventDefault()

        if (lines.length > 0 && !busy) {
          onPay()
        }

        return
      }

      if (event.key === 'Delete') {
        if (term.trim().length === 0 && picked >= 0) {
          event.preventDefault()
          removePicked()
        }

        return
      }

      // والكمّيّةُ تُزاد وتُنقص بلا فأرة — وهي أكثرُ ما يُلمس بعد المسح.
      if ((event.key === '+' || event.key === '-')
          && term.trim().length === 0 && picked >= 0 && !busy) {
        event.preventDefault()
        const line = lines[picked]
        setQty(line.variantId, line.qty + (event.key === '+' ? 1 : -1))
      }
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <div className="flex min-h-dvh flex-col">
      <div className="space-y-3 p-3">
        <ScanInput
          value={term}
          onChange={(next) => {
            setTerm(next)
            setCursor(0)
          }}
          onSubmit={onEnter}
          onArrow={moveCursor}
          onEscape={() => {
            setTerm('')
            setTyped('')
          }}
          focusKey={focusKey}
          disabled={busy}
        />

        {/* والاختصاراتُ مكتوبةٌ على الشاشة: اختصارٌ لا يعرفه أحدٌ ليس اختصارًا. */}
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-soft">
          <span><kbd>Enter</kbd> امسح/أضِف</span>
          <span><kbd>↑</kbd><kbd>↓</kbd> تنقّل</span>
          <span><kbd>Delete</kbd> احذف السطر</span>
          <span><kbd>+</kbd><kbd>-</kbd> الكمّيّة</span>
          <span><kbd>Esc</kbd> أفرِغ البحث</span>
          <span><kbd>F10</kbd> ادفع</span>
        </p>

        <ErrorBanner error={error ?? search.error} />

        {search.isFetching && <Spinner label="يُبحث…" />}

        {shown.length > 0 && (
          <ul className="grid gap-2">
            {shown.map((hit, index) => (
              <li key={hit.variantId}>
                <button
                  className={
                    'flex min-h-16 w-full items-center justify-between gap-3 rounded-xl border px-3 ' +
                    'text-right active:scale-[0.99] ' +
                    (index === at
                      ? 'border-brand bg-brand/10 ring-2 ring-brand/30'
                      : 'border-line bg-surface-raised')
                  }
                  type="button"
                  disabled={busy || hit.price === null}
                  onClick={() => add(hit)}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">{hit.productName}</span>
                    <span className="block truncate text-xs text-ink-soft">
                      {hit.label || hit.sku}
                    </span>
                  </span>

                  <span className="shrink-0 text-left">
                    <span className="tabular block font-bold" dir="ltr">
                      {hit.price === null ? 'بلا سعر' : formatMoney(hit.price, hit.currency)}
                    </span>
                    {hit.regularPrice != null && (
                      <span className="tabular block text-xs text-ink-soft" dir="ltr">
                        عرض · <s>{formatMoney(hit.regularPrice, hit.currency)}</s>
                      </span>
                    )}
                    <span
                      className={`tabular block text-xs ${hit.onHand <= 0 ? 'text-warn' : 'text-ink-soft'}`}
                      dir="ltr"
                    >
                      {formatQty(hit.onHand)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        {lines.length === 0 ? (
          <Empty>السلّةُ فارغة. امسح باركودًا أو اكتب اسمًا.</Empty>
        ) : (
          <ul className="grid gap-2 pb-3">
            {lines.map((line, index) => (
              <CartLine
                key={line.id}
                line={line}
                selected={index === picked && term.trim().length === 0}
                busy={busy}
                canOverridePrice={canOverridePrice}
                canDiscount={canDiscount}
                onQty={(qty) => setQty(line.variantId, qty)}
                onOverride={(patch) => applyOverride(line.variantId, patch)}
              />
            ))}
          </ul>
        )}
      </div>

      <footer className="sticky bottom-0 border-t border-line bg-surface p-3">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-sm text-ink-soft">المجموع</span>
          <span className="tabular text-3xl font-bold" dir="ltr">
            {formatMoney(total)}
          </span>
        </div>

        {sale && sale.header.discountTotal > 0 && (
          <p className="mb-2 text-xs text-ink-soft">
            خُصم {formatMoney(sale.header.discountTotal)}
          </p>
        )}

        <TouchButton tone="primary" wide disabled={busy || lines.length === 0} onClick={onPay}>
          ادفع
        </TouchButton>
      </footer>
    </div>
  )
}

/**
 * سطرُ السلّة — <b>والكميّةُ تُغيَّر بزرّين لا بحقل</b>: الإبهامُ أسرعُ من
 * لوحةِ مفاتيحَ تُفتح وتُغلق.
 */
function CartLine({
  line,
  selected,
  busy,
  canOverridePrice,
  canDiscount,
  onQty,
  onOverride,
}: {
  line: SaleDetail['lines'][number]
  selected: boolean
  busy: boolean
  canOverridePrice: boolean
  canDiscount: boolean
  onQty: (qty: number) => void
  onOverride: (patch: Partial<SaleLineRequest>) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <li
      className={
        'rounded-xl border p-3 ' +
        (selected ? 'border-brand bg-brand/10 ring-2 ring-brand/30' : 'border-line bg-surface-raised')
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{line.productName}</p>
          <p className="tabular truncate text-xs text-ink-soft" dir="ltr">
            {line.sku}
          </p>
        </div>

        <div className="tabular shrink-0 text-left font-bold" dir="ltr">
          {formatMoney(line.lineTotal)}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            className="min-h-11 min-w-11 rounded-lg border border-line text-xl"
            type="button"
            aria-label="أنقص"
            disabled={busy}
            onClick={() => onQty(line.qty - 1)}
          >
            −
          </button>

          <span className="tabular min-w-10 text-center text-lg font-bold" dir="ltr">
            {formatQty(line.qty)}
          </span>

          <button
            className="min-h-11 min-w-11 rounded-lg border border-line text-xl"
            type="button"
            aria-label="زِد"
            disabled={busy}
            onClick={() => onQty(line.qty + 1)}
          >
            +
          </button>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="tabular text-ink-soft" dir="ltr">
            {formatMoney(line.unitPrice)}
          </span>

          {/* ⚠ والزرُّ لا يظهر لمن لا يملك الصلاحيّة — **والخادمُ يرفض على أيّ
              حال**. والإخفاءُ راحةٌ للكاشير لا حراسة. */}
          {(canOverridePrice || canDiscount) && (
            <button
              className="min-h-11 rounded-lg border border-line px-3"
              type="button"
              disabled={busy}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? 'إخفاء' : 'تعديل'}
            </button>
          )}
        </div>
      </div>

      {line.priceSource !== 'LIST' && (
        <Banner tone="warn">
          سعرٌ يدويّ{line.listPrice === null ? ' (لا سعرَ في القائمة)' : ` بدل ${formatMoney(line.listPrice)}`}
          {line.overrideReason ? ` — ${line.overrideReason}` : ''}
        </Banner>
      )}

      {open && (
        <Override
          line={line}
          canOverridePrice={canOverridePrice}
          canDiscount={canDiscount}
          busy={busy}
          onApply={(patch) => {
            setOpen(false)
            onOverride(patch)
          }}
        />
      )}
    </li>
  )
}

/**
 * تجاوزُ السعر والخصم.
 *
 * ⚠ <b>والسببُ إلزاميٌّ للسعر لا للخصم</b> — وهو ما تفرضه القاعدةُ نفسُها:
 *   `sale_lines_override_has_reason`. والحدُّ <b>خمسةُ محارفَ حقيقيّةٍ لا
 *   مسافات</b> (قرارُ المالك)، ويُفحص هنا وفي الخادم وفي القاعدة.
 */
function Override({
  line,
  canOverridePrice,
  canDiscount,
  busy,
  onApply,
}: {
  line: SaleDetail['lines'][number]
  canOverridePrice: boolean
  canDiscount: boolean
  busy: boolean
  onApply: (patch: Partial<SaleLineRequest>) => void
}) {
  const [price, setPrice] = useState(String(line.unitPrice))
  const [discount, setDiscount] = useState(String(line.discountAmount || ''))
  const [reason, setReason] = useState(line.overrideReason ?? '')

  const priceValue = Number(price)
  const changed = Number.isFinite(priceValue) && priceValue !== line.unitPrice
  const realReason = reason.replace(/\s/g, '').length >= 5

  /**
   * ⚠ **والرفعُ فوق القائمة لا يُقاس بمقياس التنازل.**
   *
   * خفضُ السعر يُنقص ما يدخل المحلّ — فصلاحيّةٌ وسببٌ وأرضيّةُ كلفة. ورفعُه
   * لا يُنقص شيئًا: الزبونُ ساوم فلم يُعطَ خصمًا. **والمقارنةُ بسعر القائمة
   * لا بسعر السطر**: سطرٌ خُفِّض مرّةً ثمّ رُفع إلى ما دون القائمة يبقى تنازلًا.
   */
  const above = line.listPrice !== null && priceValue > line.listPrice
  const mayChangePrice = canOverridePrice || above
  const needsReason = changed && !above

  const blocked = (changed && !mayChangePrice) || (needsReason && !realReason)

  return (
    <div className="mt-3 grid gap-2 border-t border-line pt-3">
      {(canOverridePrice || line.listPrice !== null) && (
        <label className="block">
          <span className="mb-1 block text-xs text-ink-soft">
            سعرُ الوحدة
            {!canOverridePrice && line.listPrice !== null && (
              <span className="text-ink-soft">
                {' '}— الرفعُ فوق{' '}
                <span className="tabular" dir="ltr">{formatMoney(line.listPrice)}</span>
                {' '}مسموح، والخفضُ يحتاج صلاحيّة
              </span>
            )}
          </span>
          <input
            className="tabular min-h-12 w-full rounded-lg border border-line bg-surface px-3"
            dir="ltr"
            inputMode="decimal"
            value={price}
            disabled={busy}
            onChange={(event) => setPrice(event.target.value)}
          />
        </label>
      )}

      {canDiscount && (
        <label className="block">
          <span className="mb-1 block text-xs text-ink-soft">خصمٌ على السطر (مبلغ)</span>
          <input
            className="tabular min-h-12 w-full rounded-lg border border-line bg-surface px-3"
            dir="ltr"
            inputMode="decimal"
            value={discount}
            disabled={busy}
            onChange={(event) => setDiscount(event.target.value)}
          />
        </label>
      )}

      {needsReason && (
        <label className="block">
          <span className="mb-1 block text-xs text-ink-soft">سببُ خفضِ السعر</span>
          <input
            className="min-h-12 w-full rounded-lg border border-line bg-surface px-3"
            value={reason}
            disabled={busy}
            placeholder="خمسةُ محارفَ فأكثر"
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      )}

      <TouchButton
        wide
        tone="primary"
        disabled={busy || blocked}
        onClick={() =>
          onApply({
            unitPrice: changed ? priceValue : undefined,

            // والسببُ يُرسَل للخفض وحدَه — والخادمُ يرفض سببًا لا محلَّ له.
            overrideReason: needsReason ? reason.trim() : undefined,
            discountAmount: Number(discount) || undefined,
          })
        }
      >
        طبّق
      </TouchButton>

      {blocked && <p className="text-xs text-warn">السببُ مطلوبٌ بخمسةِ محارفَ حقيقيّةٍ فأكثر.</p>}
    </div>
  )
}
